import React, { useEffect } from 'react';
import { Activity, Heart, Zap, Shield, Users, CheckCircle, ArrowRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
declare global {
  interface Window {
    Calendly?: {
      initPopupWidget: (options: {
        url: string;
      }) => void;
    };
  }
}
export default function TestosteroneReplacementTherapyTrt() {
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.type = 'text/javascript';
    script.async = true;
    document.head.appendChild(script);
    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);
  const handleCalendlyClick = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: 'https://calendly.com/mfloresdo/30min'
      });
    }
  };
  const symptoms = ['Low energy, fatigue, or burnout', 'Low libido, ED, or sexual performance issues', 'Stubborn belly fat or poor metabolism', 'Brain fog, poor focus, or low motivation', 'Poor recovery, muscle loss, or strength decline', 'Feeling older than you should'];
  const processSteps = [{
    title: 'Medical Intake & Labs',
    description: 'We review your symptoms, medical history, lifestyle, medications, goals, and lab data to understand the full picture.'
  }, {
    title: 'Physician-Led Treatment Plan',
    description: 'If treatment is appropriate, we build a plan around your physiology, goals, and risk profile — not a cookie-cutter protocol.'
  }, {
    title: 'Ongoing Optimization',
    description: 'Follow-up labs, monitoring, and adjustments help keep your plan effective, responsible, and aligned with long-term health.'
  }];
  const planPaths = [{
    name: 'Basecamp',
    subtitle: 'Hormone Foundation',
    description: 'Best for men primarily focused on testosterone, energy, libido, recovery, and foundational hormone optimization.'
  }, {
    name: 'Ascent',
    subtitle: 'Hormones + Body Composition',
    description: 'Best for men who want hormone optimization plus weight loss, metabolic support, injectable wellness options, or GLP-1 access when appropriate.'
  }, {
    name: 'Summit',
    subtitle: 'Full Performance Optimization',
    description: 'Best for men who want a more comprehensive approach to hormones, metabolic health, recovery, longevity, and performance.'
  }];
  const services = [{
    icon: Activity,
    title: 'Testosterone & Hormone Optimization',
    description: 'For men with symptoms of low or suboptimal testosterone, including fatigue, low libido, poor recovery, and muscle loss.'
  }, {
    icon: Heart,
    title: 'Weight Loss & Metabolic Health',
    description: 'Support for stubborn belly fat, appetite control, insulin resistance, body composition, and long-term metabolic health.'
  }, {
    icon: Zap,
    title: 'Sexual Health & Performance',
    description: 'Evaluation and treatment options for ED, low libido, confidence, and sexual performance concerns.'
  }, {
    icon: Shield,
    title: 'Peptides, NAD+ & Injectable Wellness',
    description: 'When clinically appropriate, advanced therapies may be used to support recovery, energy, cellular health, and performance.'
  }, {
    icon: Users,
    title: 'Lifestyle & Supplement Guidance',
    description: 'Practical support around training, nutrition, sleep, supplements, and habits that make medical therapy work better.'
  }, {
    icon: CheckCircle,
    title: 'Advanced Labs & Monitoring',
    description: 'Lab-guided care designed to monitor safety, optimize treatment, and identify issues that standard wellness visits often miss.'
  }];
  const fitItems = ['You want physician-led care instead of a national TRT mill', 'You are willing to complete labs and follow-up monitoring', 'You want long-term optimization, not reckless high-dose protocols', 'You live in Texas and prefer convenient telemedicine care'];
  const notFitItems = ['You want testosterone without labs or medical evaluation', 'You want bodybuilding-style dosing', 'You are unwilling to monitor bloodwork', 'You are looking for insurance-based primary care'];
  return <section className="py-6 px-6 bg-background">
      <div className="container">
        <div className="max-w-5xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary-light-bg px-4 py-2 text-sm font-medium text-primary mb-5">
              Physician-Led Men’s Health Optimization for Texas Men
            </div>

            <h1 className="text-4xl md:text-5xl font-semibold text-foreground mb-4 leading-tight">
              Testosterone, Weight Loss, and Performance Medicine Built Around Your Labs
            </h1>

            <p className="text-lg md:text-xl text-muted-dark max-w-4xl mx-auto leading-relaxed">
              If you’re dealing with low energy, stubborn belly fat, low libido, poor recovery, or brain fog, IronPeak helps identify what’s driving it and builds a medically supervised plan around your goals.
            </p>

            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button onClick={handleCalendlyClick} size="lg" className="bg-accent text-accent-foreground hover:bg-accent-hover active:bg-accent-active hover:-translate-y-0.5 hover:shadow-medium active:translate-y-0 transition-all duration-150 ease-[cubic-bezier(0.34,1.56,0.64,1)] focus-visible:ring-accent/40">
                Schedule Your Consultation
              </Button>

              <a href="/pricing">
                <Button type="button" size="lg" variant="outline" className="border-primary/30 text-primary hover:bg-primary-light-bg">
                  View Pricing
                </Button>
              </a>
            </div>

            <p className="text-sm text-muted-dark mt-4">
              No pressure. Medical evaluation first. Treatment only if appropriate.
            </p>
          </div>

          {/* Symptoms */}
          <div className="mb-12">
            <Card className="card border-2 border-muted-light shadow-soft bg-primary-light-bg">
              <CardHeader className="pb-4">
                <CardTitle className="text-2xl md:text-3xl font-semibold text-foreground text-center">
                  Common Reasons Men Come to IronPeak
                </CardTitle>
              </CardHeader>

              <CardContent>
                <ul className="text-left text-base md:text-lg text-foreground list-none grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 max-w-4xl mx-auto">
                  {symptoms.map(symptom => <li key={symptom} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 mt-1 shrink-0" />
                      <span>{symptom}</span>
                    </li>)}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Reframe */}
          <div className="mb-12">
            <Card className="card p-8 border-2 border-muted-light">
              <CardContent className="p-0 text-center max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4">
                  You Don’t Need to Guess Which Treatment You Need
                </h2>

                <p className="text-base md:text-lg text-muted-dark leading-relaxed mb-4">
                  Low energy, weight gain, low libido, poor recovery, and brain fog can come from several overlapping issues: hormones, sleep, insulin resistance, nutrient deficiencies, stress, medications, and cardiovascular risk.
                </p>

                <p className="text-base md:text-lg text-muted-dark leading-relaxed mb-4">
                  IronPeak does not start with a prescription. We start with a medical evaluation, targeted labs, and a clear plan.
                </p>

                <p className="text-base md:text-lg text-foreground font-medium leading-relaxed">
                  The goal is not more medication. The goal is the right intervention for the right man.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* How It Works */}
          <div className="mb-12">
            <Card className="card p-8 border-2 border-muted-light">
              <CardHeader className="pb-5">
                <CardTitle className="text-3xl md:text-4xl font-semibold text-foreground text-center">
                  How It Works
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-5 text-left max-w-4xl mx-auto">
                {processSteps.map((step, index) => <div key={step.title} className="flex gap-4">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold">
                      {index + 1}
                    </div>

                    <div>
                      <h4 className="text-lg md:text-xl font-semibold text-foreground mb-1">
                        {step.title}
                      </h4>
                      <p className="text-base md:text-lg text-muted-dark leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>)}
              </CardContent>
            </Card>
          </div>

          {/* Guarantee */}
              <div className="mb-12">
                <Card className="card border-2 border-accent/30 bg-accent/5 shadow-soft overflow-hidden">
                  <CardContent className="p-6 md:p-8">
                    <div className="max-w-3xl mx-auto text-center">
                      <h3 className="text-2xl md:text-3xl font-semibold text-foreground mb-4">
                        30-Day Satisfaction Guarantee
                      </h3>
              
                      <img src="https://ironpeakmenshealth.com/wp-content/uploads/2026/05/30-day-guarantee.png" alt="IronPeak Men's Health 30-Day Satisfaction Guarantee" className="mx-auto w-full max-w-2xl rounded-xl shadow-soft" loading="lazy" />
              
                      <p className="text-base md:text-lg text-muted-dark max-w-3xl mx-auto leading-relaxed mt-6">
                        If you complete your initial evaluation and feel IronPeak is not the right fit, let us know within 30 days. We’ll make it right or your money back.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

          {/* Plan Paths */}
          <div className="mb-12">
            <div className="text-center mb-7">
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-3">
                Start With the Right Path
              </h2>

              <p className="text-base md:text-lg text-muted-dark max-w-3xl mx-auto leading-relaxed">
                Your plan may include testosterone therapy, GLP-1 medication, ED treatment, NAD+, B12, peptides, supplement guidance, or advanced lab monitoring — depending on your labs, symptoms, and goals.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {planPaths.map(plan => <Card key={plan.name} className="card border-2 border-muted-light shadow-soft h-full">
                  <CardContent className="p-6">
                    <h3 className="text-2xl font-semibold text-foreground mb-1">
                      {plan.name}
                    </h3>

                    <p className="text-primary font-medium mb-3">
                      {plan.subtitle}
                    </p>

                    <p className="text-muted-dark leading-relaxed">
                      {plan.description}
                    </p>
                  </CardContent>
                </Card>)}
            </div>

            <div className="mt-7 flex justify-center">
              <a href="/pricing">
                <Button type="button" size="lg" variant="outline" className="border-primary/30 text-primary hover:bg-primary-light-bg">
                  Compare Plans
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>

          {/* Services */}
          <div className="mb-12">
            <div className="text-center mb-7">
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-3">
                Services We May Use in Your Plan
              </h2>

              <p className="text-base md:text-lg text-muted-dark max-w-3xl mx-auto leading-relaxed">
                These are not one-size-fits-all products. They are clinical tools that may be used when appropriate based on your evaluation.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {services.map(service => {
              const Icon = service.icon;
              return <Card key={service.title} className="card border-2 border-muted-light shadow-soft h-full">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="rounded-xl bg-primary-light-bg p-3">
                          <Icon className="h-6 w-6 text-primary" />
                        </div>

                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-2">
                            {service.title}
                          </h3>

                          <p className="text-muted-dark leading-relaxed">
                            {service.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>;
            })}
            </div>
          </div>

          {/* Why IronPeak */}
          <div className="mb-12">
            <Card className="card p-8 border-2 border-muted-light bg-primary-light-bg">
              <CardHeader className="pb-5">
                <CardTitle className="text-3xl md:text-4xl font-semibold text-foreground text-center">
                  Why Men Choose IronPeak
                </CardTitle>
              </CardHeader>

              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <h4 className="text-lg font-semibold text-foreground mb-1">
                    Physician-Led Care
                  </h4>
                  <p className="text-muted-dark leading-relaxed">
                    Your plan is reviewed through a medical lens, not built around a call-center script.
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-foreground mb-1">
                    Labs Interpreted in Context
                  </h4>
                  <p className="text-muted-dark leading-relaxed">
                    We consider symptoms, trends, risk factors, and goals — not just whether you fall inside a lab reference range.
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-foreground mb-1">
                    Ongoing Optimization
                  </h4>
                  <p className="text-muted-dark leading-relaxed">
                    Treatment is monitored and adjusted over time for safety, progress, and long-term results.
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-foreground mb-1">
                    Texas-Based Telemedicine
                  </h4>
                  <p className="text-muted-dark leading-relaxed">
                    Convenient care for men across Texas, with transparent pricing and no insurance billing.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Fit / Not Fit */}
          <div className="mb-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Card className="card border-2 border-muted-light shadow-soft">
                <CardHeader>
                  <CardTitle className="text-2xl font-semibold text-foreground">
                    IronPeak May Be a Good Fit If You:
                  </CardTitle>
                </CardHeader>

                <CardContent>
                  <ul className="space-y-3">
                    {fitItems.map(item => <li key={item} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-primary mr-3 mt-0.5 shrink-0" />
                        <span className="text-muted-dark">{item}</span>
                      </li>)}
                  </ul>
                </CardContent>
              </Card>

              <Card className="card border-2 border-muted-light shadow-soft">
                <CardHeader>
                  <CardTitle className="text-2xl font-semibold text-foreground">
                    IronPeak May Not Be the Right Fit If You:
                  </CardTitle>
                </CardHeader>

                <CardContent>
                  <ul className="space-y-3">
                    {notFitItems.map(item => <li key={item} className="flex items-start">
                        <span className="text-primary mr-3 font-bold text-xl leading-5">•</span>
                        <span className="text-muted-dark">{item}</span>
                      </li>)}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Final CTA */}
          <div className="text-center mb-6">
            <Card className="card border-2 border-primary/20 bg-primary-light-bg shadow-soft">
              <CardContent className="p-8">
                <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-3">
                  Not Sure Where to Start?
                </h2>

                <p className="text-base md:text-lg text-muted-dark max-w-3xl mx-auto leading-relaxed mb-6">
                  You do not need to know whether you need TRT, GLP-1 therapy, peptides, NAD+, or supplements. That is what the evaluation is for. We review your symptoms, labs, goals, and medical history, then recommend a plan only if it makes clinical sense.
                </p>

                <Button onClick={handleCalendlyClick} size="lg" className="bg-accent text-accent-foreground hover:bg-accent-hover active:bg-accent-active hover:-translate-y-0.5 hover:shadow-medium active:translate-y-0 transition-all duration-150 ease-[cubic-bezier(0.34,1.56,0.64,1)] focus-visible:ring-accent/40">
                  Schedule Your Consultation
                </Button>

                <p className="text-sm text-muted-dark mt-4">
                  No pressure. Medical evaluation first. Treatment only if appropriate.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}